import { capture } from '../analytics';
import { bboxToLatLngs } from '../geo';
import type { SelectedSiteGeometry } from '../selectedSiteExtrusion';
import type { BuildingNeighbor, SiteRow } from '../types';
import { sb } from './client';
import type {
  Category,
  CreateSiteArgs,
  ReconcileAssetsResult,
  SelectedSiteLod1Row,
  SiteBuildingContextRow,
  SitesLiveRow,
  SiteUpdate,
} from './types';

function isSelectedSiteGeometry(value: unknown): value is SelectedSiteGeometry {
  if (!value || typeof value !== 'object') return false;
  const candidate = value as { type?: unknown; coordinates?: unknown };
  const polygons = candidate.type === 'Polygon'
    ? [candidate.coordinates]
    : candidate.type === 'MultiPolygon'
      ? candidate.coordinates
      : null;
  if (!Array.isArray(polygons) || polygons.length === 0) return false;
  return polygons.every((polygon) => {
    if (!Array.isArray(polygon) || !Array.isArray(polygon[0]) || polygon[0].length < 3) return false;
    return polygon[0].every(
      (point) =>
        Array.isArray(point) &&
        Number.isFinite(point[0]) &&
        Number.isFinite(point[1]) &&
        point[0] >= -180 &&
        point[0] <= 180 &&
        point[1] >= -90 &&
        point[1] <= 90,
    );
  });
}

/** Normalize the single-row selected-site LoD1 RPC contract. */
export function rowToSelectedSiteLod1(row: unknown, fallbackSiteId?: string): SelectedSiteLod1Row | null {
  if (!row || typeof row !== 'object' || Array.isArray(row)) return null;
  const r = row as Record<string, unknown>;
  const siteId = typeof r.site_id === 'string' ? r.site_id : fallbackSiteId;
  const buildingId = typeof r.open_building_id === 'string' || typeof r.open_building_id === 'number'
    ? String(r.open_building_id)
    : null;
  const geometry = r.geometry ?? r.geom;
  if (
    typeof siteId !== 'string' ||
    buildingId === null ||
    !isSelectedSiteGeometry(geometry)
  ) {
    return null;
  }
  const finiteOrNull = (value: unknown): number | null =>
    typeof value === 'number' && Number.isFinite(value) ? value : null;
  const stringOrNull = (value: unknown): string | null => (typeof value === 'string' ? value : null);
  return {
    site_id: siteId,
    open_building_id: buildingId,
    geometry,
    height_m: finiteOrNull(r.height_m),
    uncertainty_raw: r.uncertainty_raw ?? null,
    gba_id: stringOrNull(r.gba_id),
    source: stringOrNull(r.source ?? r.gba_source),
    region: stringOrNull(r.region ?? r.gba_region),
    source_revision: stringOrNull(r.source_revision),
    match_method: stringOrNull(r.match_method),
    match_iou: finiteOrNull(r.match_iou),
    centroid_distance_m: finiteOrNull(r.centroid_distance_m),
    attribution: stringOrNull(r.attribution),
  };
}

/** Fetch the optional GBA LoD1 match for one selected site. */
export async function getSelectedSiteLod1(siteId: string): Promise<SelectedSiteLod1Row | null> {
  const c = sb();
  if (!c || !siteId) return null;
  const { data, error } = await c.rpc('get_selected_site_lod1', { p_site_id: siteId });
  if (error) throw error;
  const row = Array.isArray(data) ? data[0] : data;
  return rowToSelectedSiteLod1(row, siteId);
}

/** Normalize an optional context RPC row without allowing malformed geometry into WebGL. */
export function rowToBuildingNeighbor(row: SiteBuildingContextRow): BuildingNeighbor | null {
  if (!row || typeof row !== 'object') return null;
  const geometry = row.footprint ?? row.geom ?? row.display_geom ?? row.bbox;
  let footprint: [number, number][] | null;
  try {
    footprint = bboxToLatLngs(geometry);
  } catch {
    return null;
  }
  const rawHeight = row.display_height_m ?? row.height_m;
  const heightM = typeof rawHeight === 'number' && Number.isFinite(rawHeight) && rawHeight > 0
    ? rawHeight
    : null;

  if (
    !footprint ||
    footprint.length < 3 ||
    !footprint.every(
      ([lat, lng]) =>
        Number.isFinite(lat) && Number.isFinite(lng) && lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180,
    ) ||
    (heightM !== null && (heightM < 0 || !Number.isFinite(heightM)))
  ) {
    return null;
  }
  return {
    id: String(row.building_id ?? row.open_building_id ?? row.id ?? ''),
    footprint,
    heightM: heightM === null ? null : Math.max(0, Math.min(100, heightM)),
    distanceM: typeof row.distance_m === 'number' && Number.isFinite(row.distance_m) ? row.distance_m : undefined,
    isTarget: row.is_target === true,
  };
}

/** Fetch nearby height-aware footprints only when the 3D viewer is requested. */
export async function getSiteBuildingContext(siteId: string, radiusM = 300): Promise<BuildingNeighbor[]> {
  const c = sb();
  if (!c) return [];
  const boundedRadius = Math.max(25, Math.min(500, Number.isFinite(radiusM) ? radiusM : 150));
  try {
    const { data, error } = await c.rpc('get_site_building_context', {
      p_site_id: siteId,
      p_radius_m: boundedRadius,
    });
    // The context RPC is additive; older databases simply omit it.
    if (error || !Array.isArray(data)) return [];
    return (data as SiteBuildingContextRow[])
      .map(rowToBuildingNeighbor)
      .filter(
        (neighbor): neighbor is BuildingNeighbor => neighbor !== null && neighbor.isTarget !== true,
      );
  } catch {
    return [];
  }
}

/** Normalize a get_sites_live row to the named SiteRow consumed by enrich(). */
export function rowToSiteRow(r: SitesLiveRow): SiteRow {
  const loc = r.location;
  const lng = loc?.coordinates?.[0] ?? 0;
  const lat = loc?.coordinates?.[1] ?? 0;
  const phase = r.phase || 'existing';
  const area = r.roof_area_sqm || 0;
  const conf = r.confidence_pct ?? Math.round((r.ai_confidence || 0) * 100);
  const sub = (r.location_name || r.address || '').trim();
  const updated = r.last_update || '';
  const bbox = bboxToLatLngs(r.bbox ?? r.display_geom);
  const displayHeightM = r.display_height_m ?? r.height_m ?? null;
  const heightSource = r.display_height_source ?? r.height_source ?? null;
  const heightYear = r.display_height_year ?? r.height_year ?? null;
  const heightMethod = r.display_height_method ?? r.height_method ?? null;
  const heightQuality = r.display_height_quality ?? r.height_quality ?? null;
  const heightPixels = r.display_height_valid_pixel_count ?? null;
  return {
    id: r.id,
    site_name: r.site_name || '',
    location_name: sub,
    // Keep the historical safe default for incomplete/legacy rows: a row
    // without either asset flag was treated as a solar site.
    has_solar: !!r.has_solar || (!r.has_solar && !r.has_cabinet),
    has_cabinet: !!r.has_cabinet,
    phase,
    roof_area_sqm: area,
    confidence_pct: conf,
    lat,
    lng,
    last_update: updated,
    bbox,
    status: r.status || '',
    display_height_m: typeof displayHeightM === 'number' && Number.isFinite(displayHeightM) ? displayHeightM : null,
    estimated_stories: typeof r.estimated_stories === 'number' && Number.isFinite(r.estimated_stories)
      ? r.estimated_stories
      : null,
    display_height_year: typeof heightYear === 'number' && Number.isFinite(heightYear) ? heightYear : null,
    display_height_source: heightSource,
    display_height_quality: typeof heightQuality === 'number' && Number.isFinite(heightQuality)
      ? heightQuality
      : null,
    display_height_method: heightMethod,
    display_height_valid_pixel_count: typeof heightPixels === 'number' && Number.isFinite(heightPixels) ? heightPixels : null,
    auto_open_building_id: r.auto_open_building_id == null ? null : String(r.auto_open_building_id),
    display_open_building_id: r.display_open_building_id == null
      ? (r.auto_open_building_id == null ? null : String(r.auto_open_building_id))
      : String(r.display_open_building_id),
    geometry_source: r.geometry_source ?? r.display_geometry_source ?? null,
    geometry_source_version: r.geometry_source_version ?? r.display_geometry_source_version ?? null,
    gers_id: r.gers_id ?? r.display_gers_id ?? null,
    display_pvout_kwh_kwp_year: r.display_pvout_kwh_kwp_year ?? r.pvout_kwh_kwp_year ?? null,
    display_ghi_kwh_m2_year: r.display_ghi_kwh_m2_year ?? r.ghi_kwh_m2_year ?? null,
    display_solar_source: r.display_solar_source ?? r.solar_source ?? null,
    display_solar_source_version: r.display_solar_source_version ?? r.solar_source_version ?? null,
    display_model_version: r.display_model_version ?? r.model_version ?? null,
  };
}

export async function getSitesLive(): Promise<SiteRow[]> {
  const c = sb();
  if (!c) return [];
  const { data, error } = await c.rpc('get_sites_live');
  if (error || !data) {
    if (error) throw error;
    return [];
  }
  return (data as SitesLiveRow[]).map(rowToSiteRow);
}

export async function createSite(
  args: CreateSiteArgs,
): Promise<{ id: string } & Record<string, unknown>> {
  const c = sb();
  if (!c) throw new Error('Not connected');
  const { data, error } = await c.rpc('create_site', args);
  if (error) throw error;
  return data as { id: string } & Record<string, unknown>;
}

export function categoryForPhase(phase: string): Category {
  return phase === 'existing' ? 'Existing' : phase === 'planned' ? 'Phase 2' : 'Next';
}

export async function updateSite(id: string, fields: SiteUpdate): Promise<void> {
  const c = sb();
  if (!c) throw new Error('Not connected');
  const { error } = await c.from('sites').update(fields).eq('id', id);
  if (error) throw error;
}

/** Reconcile a site's solar_array / swap_cabinet asset rows atomically via RPC. */
export async function syncSiteAssets(
  id: string,
  solar: boolean,
  cabinet: boolean,
): Promise<ReconcileAssetsResult> {
  const c = sb();
  if (!c) throw new Error('Not connected');
  const { data, error } = await c.rpc('reconcile_site_assets', {
    p_site_id: id,
    p_has_solar: solar,
    p_has_cabinet: cabinet,
  });
  if (error) throw error;
  return data as ReconcileAssetsResult;
}

export async function deleteSite(id: string): Promise<void> {
  const c = sb();
  if (!c) throw new Error('Not connected');
  const { error } = await c.from('sites').delete().eq('id', id);
  if (error) throw error;
}

/** Kick off OSM/Overpass building detection for a freshly-created site. */
export async function fetchFootprint(siteId: string): Promise<Record<string, unknown> | null> {
  const c = sb();
  if (!c) return null;
  try {
    const { data, error } = await c.functions.invoke('fetch-footprint', {
      body: { site_id: siteId },
    });
    if (error) throw error;
    const d = (data as Record<string, unknown>) ?? null;
    const found = Number(d?.footprints_found ?? 0);
    capture('footprint_fetched', { site_id: siteId, has_roof: found > 0, buildings_found: found });
    return d;
  } catch (err) {
    capture('footprint_fetch_failed', {
      site_id: siteId,
      error: (err as Record<string, unknown>)?.message ?? String(err),
    });
    throw err; // preserve caller behaviour (callers swallow with .catch)
  }
}
